import { FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { css } from '@emotion/react';
import CollectionCard from '../CollectionCard';
import Button from '../Button';
import { spacing } from '../../styles';
import { CollectionCardsEvent } from '../../types/streaming';
import { getCollectionImages } from '../../utils/getImages';
import * as stores from '../../stores';

interface Props {
  collectionCardsData: CollectionCardsEvent;
}

const ChatCollectionCards: FC<Props> = ({ collectionCardsData }) => {
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const [imageMap, setImageMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const updateImages = () => {
      const newImageMap: Record<string, string> = {};
      collectionCardsData.cards?.forEach((collection) => {
        // Use image_id directly from backend data
        const imageId = collection.image_id;
        if (imageId) {
          const imageSrc = getCollectionImages(imageId);
          if (imageSrc) {
            newImageMap[imageId] = imageSrc;
          }
        }
      });
      setImageMap(newImageMap);
    };

    updateImages();

    window.addEventListener('themeChanged', updateImages);
    return () => window.removeEventListener('themeChanged', updateImages);
  }, [collectionCardsData]);

  const renderCollectionCard = (collection: any) => {
    // Use image_id directly from backend data
    const imageId = collection.image_id;
    const imageSrc = imageMap[imageId ?? ''];

    if (!imageSrc) return null;

    const isRestricted =
      collection.restricted && !purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

    const cardComponent = <CollectionCard name={collection.name} imageSrc={imageSrc} isLocked={isRestricted} />;

    return (
      <div key={collection.id} css={styles.item}>
        <div css={styles.itemContent}>
          <div css={styles.cardWrapper}>
            {isRestricted ? (
              <Button
                onClick={() => stores.showPaymentModalByPreference('chat_page_collection')}
                css={css`
                  height: 100%;
                  width: 100%;
                `}
              >
                {cardComponent}
              </Button>
            ) : (
              <Link to={`/collection/${collection.id}`}>{cardComponent}</Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  const styles = {
    horizontalList: css`
      display: flex;
      overflow-x: auto;
      gap: ${spacing.deci};
      padding: ${spacing.centi} 0;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    `,
    item: css`
      flex: 0 0 auto;
      width: 140px;
      position: relative;

      &::before {
        content: '';
        padding-bottom: 120%;
        display: block;
      }
    `,
    itemContent: css`
      position: absolute;
      top: 0;
      height: 100%;
      width: 100%;
      text-align: center;
    `,
    cardWrapper: css`
      width: 100%;
      height: 100%;
    `,
  };

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0;
      `}
    >
      <div css={styles.horizontalList}>{collectionCardsData.cards.map((card) => renderCollectionCard(card))}</div>
    </div>
  );
};

export default ChatCollectionCards;
